import { useState } from 'react';
import mondaySdk from 'monday-sdk-js';
import { type PortalSettings, type FieldValues, type BoardFiltersFieldValue } from '@gorilla/portal-shared/src/schemas';
import { BoardFiltersFieldDescriptor, SectionDescriptor } from '@gorilla/portal-shared/src/types';
import { BoardRulesModal } from '@gorilla/ui/src/components/BoardRulesModal';
import { areBoardFiltersValid, hasBoardFilters } from '@gorilla/common/src/lib/rules';
import { Box, Flex, Button, Text, Icon, Badge, Tooltip, Label } from '@vibe/core';
import { Filter as FilterIcon, Board as BoardIcon, Subitems as SubitemsIcon, ExternalPage } from '@vibe/icons';
import { useBoardSourceResolver } from '../../hooks/use-board-source-resolver';

type BoardFiltersFieldProps = {
  value: BoardFiltersFieldValue;
  fieldDescriptor: BoardFiltersFieldDescriptor;
  sectionDescriptor: SectionDescriptor;
  portalSettings: PortalSettings;
  sectionFieldValues: FieldValues;
  clientBoardSubitemsBoardId: number | null;
  onChange: (value: BoardFiltersFieldValue) => void;
};

export function BoardFiltersField({
  value,
  fieldDescriptor,
  portalSettings,
  onChange,
  sectionDescriptor,
  sectionFieldValues,
  clientBoardSubitemsBoardId,
}: BoardFiltersFieldProps) {
  const monday = mondaySdk();
  const [showBoardFilters, setShowBoardFilters] = useState(false);
  const { board, isLoading, boardSourceType } = useBoardSourceResolver({
    portalSettings,
    sectionDescriptor,
    clientBoardSubitemsBoardId,
    sectionFieldValues,
  });

  if (isLoading || !board) {
    return null;
  }

  return (
    <>
      {showBoardFilters ? (
        <BoardRulesModal
          isOpen={showBoardFilters}
          hideCondition={true}
          board={board}
          inititalBoardFilters={value || {}}
          onClose={(newBoardFilters) => {
            if (newBoardFilters !== null && newBoardFilters !== undefined) {
              // always set the operator to 'and' when saving for now
              newBoardFilters.operator = 'and';
              onChange(newBoardFilters);
            }

            setShowBoardFilters(false);
          }}
        />
      ) : null}
      <Box
        className="section__field section__field--boardsource"
        border={true}
        borderColor="layoutBorderColor"
        backgroundColor="greyBackgroundColor"
        style={{
          borderTopRightRadius: '4px',
          borderTopLeftRadius: '4px',
        }}
      >
        <Box padding="medium">
          <Flex
            gap={16}
            style={{
              width: '100%',
              maxWidth: '100%',
            }}
          >
            <Flex
              gap={8}
              style={{
                flex: 1,
                maxWidth: 'calc(100% - 148px)',
              }}
            >
              <div
                onClick={() => {
                  monday.execute('openLinkInTab', { url: board.url });
                }}
                className="board__link__external"
              >
                <Icon
                  icon={boardSourceType === 'subitems' ? SubitemsIcon : BoardIcon}
                  size={24}
                  style={{
                    transform: 'translate3d(0px,1px,0)',
                  }}
                  className="board__link__external--default"
                />
                <Icon
                  icon={ExternalPage}
                  size={21}
                  style={{
                    transform: 'translate3d(0px,1px,0)',
                  }}
                  className="board__link__external--hover"
                />
              </div>
              <Text
                type="text1"
                weight="bold"
                align="start"
                ellipsis={true}
                withoutTooltip
                style={{
                  //flex: 1,
                  marginRight: '8px',
                }}
              >
                {board.name}
              </Text>
              {boardSourceType === 'subitems' && (
                <Tooltip
                  content="Only up to 100 subitems can be fetched and displayed in your portal due to monday API limits."
                  position="right"
                  showOnDialogEnter={false}
                >
                  <Label text="Limited to 100" color="dark" kind="line" />
                </Tooltip>
              )}
            </Flex>

            <Box style={{ overflow: 'visible', flex: 0, marginLeft: 'auto' }}>
              <Flex gap={10}>
                <Flex gap={16}>
                  {hasBoardFilters(board, value) ? (
                    <Badge
                      // @ts-ignore
                      color={!areBoardFiltersValid(board, value) ? 'notification' : 'primary'}
                    >
                      <Button
                        leftIcon={FilterIcon}
                        kind="secondary"
                        size="small"
                        color={!areBoardFiltersValid(board, value) ? 'negative' : 'primary'}
                        onClick={() => {
                          setShowBoardFilters(true);
                        }}
                        className="custom__deletepagebutton"
                        style={{
                          paddingRight: '12px',
                        }}
                      >
                        {!areBoardFiltersValid(board, value) ? 'Invalid filter' : 'Filter + Sorting'}
                      </Button>
                    </Badge>
                  ) : (
                    <Tooltip
                      content="Apply filter rules and sorting to board items shown to clients"
                      position="top"
                      showOnDialogEnter={false}
                    >
                      <Button
                        leftIcon={FilterIcon}
                        kind="tertiary"
                        size="small"
                        color="primary"
                        onClick={() => {
                          setShowBoardFilters(true);
                        }}
                        className="custom__deletepagebutton"
                      >
                        Filter + Sorting
                      </Button>
                    </Tooltip>
                  )}
                </Flex>
              </Flex>
            </Box>
          </Flex>
        </Box>
      </Box>
    </>
  );
}
